// Users & Roles management + Audit log + Settings (org/branding + renewal reminders). Phase 8.
import { useEffect, useMemo, useState, useRef } from "react";
import { api, API, formatApiError } from "@/lib/api";
import RoleChip from "@/components/RoleChip";
import Pill from "@/components/Pill";
import { TID } from "@/lib/testIds";
import { Mail, ShieldCheck, Plus, Pencil, Trash2, KeyRound, X, Save, Loader2, CalendarClock, Search, Send, Inbox, RefreshCw, CheckCircle2, Clock, AlertCircle, CheckSquare, FileText, Upload, Tags, Activity, Eye, Radar, QrCode, LogIn, LogOut, AlertTriangle, HardHat, Sparkles, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
    AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { ROLE_LABELS } from "@/lib/constants";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import { useAuditBrowser } from "@/hooks/useAuditBrowser";
import { AuditLogFilters, AuditLogPagination } from "@/components/AuditLogControls";
import RoleLabelsPanel from "@/components/RoleLabelsPanel";
import AuditAskPanel from "@/components/AuditAskPanel";
import ReviewRoutingPanel from "@/components/ReviewRoutingPanel";
import { EmailNotificationSwitches } from "@/components/EmailNotificationSwitches";

const ADMIN_ROLES = new Set(["director", "administrator"]);
const INTERNAL = new Set(["director", "administrator", "consultant"]);

export function UsersPage() {
    const { user } = useAuth();
    const canManage = user && ADMIN_ROLES.has(user.role);
    const [tab, setTab] = useState("users");
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [roleLabels, setRoleLabels] = useState([]);
    const [dialog, setDialog] = useState(null);
    const [result, setResult] = useState(null);
    const [bulkResult, setBulkResult] = useState(null);
    const [selected, setSelected] = useState([]);
    const [busyBulk, setBusyBulk] = useState(false);
    const [q, setQ] = useState("");
    const [roleFilter, setRoleFilter] = useState("All");

    const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    useEffect(() => {
        load();
        api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
        if (canManage) api.get("/role-labels").then((r) => setRoleLabels(r.data)).catch(() => setRoleLabels([]));
    }, [canManage]);
    const labelById = useMemo(() => Object.fromEntries(roleLabels.map((l) => [l.id, l.name])), [roleLabels]);

    async function deactivate(u) {
        if (!window.confirm(`Deactivate ${u.name}? They will lose access.`)) return;
        try { await api.delete(`/users/${u.id}`); load(); toast.success("User deactivated"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }

    async function sendReset(u) {
        if (!window.confirm(`Send a password reset link to ${u.name} (${u.email})?`)) return;
        try {
            const { data } = await api.post(`/users/${u.id}/send-reset`);
            setResult({ mode: "reset", email: data.email, reset_link: data.reset_link, email_status: data.email_status });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }

    async function sendCredentials(u) {
        if (!window.confirm(`Email login details to ${u.name} (${u.email})?\n\nThis resets them to a new temporary password which they'll change on next login.`)) return;
        try {
            const { data } = await api.post(`/users/${u.id}/send-credentials`);
            setResult({ mode: "credentials", email: data.email, temp_password: data.temp_password, email_status: data.email_status });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }

    function toggleSelect(id) {
        setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
    }

    async function bulkSendCredentials(ids) {
        if (!ids.length) return;
        if (!window.confirm(`Email login details to ${ids.length} user${ids.length > 1 ? "s" : ""}?\n\nEach is reset to a new temporary password which they'll change on next login.`)) return;
        setBusyBulk(true);
        try {
            const { data } = await api.post(`/users/send-credentials`, { user_ids: ids });
            setBulkResult(data);
            setSelected([]);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusyBulk(false); }
    }

    const filtered = users.filter((u) => {
        if (roleFilter !== "All" && u.role !== roleFilter) return false;
        if (q && !`${u.name} ${u.email} ${u.title || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });
    const accessors = useMemo(() => ({
        name: (u) => u.name || "",
        email: (u) => u.email || "",
        role: (u) => ROLE_LABELS[u.role] || u.role || "",
        title: (u) => u.title || "",
        last_login: (u) => u.last_login_at || "",
    }), []);
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, { accessors });

    return (
        <div data-testid={TID.usersPage} className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    {canManage && (
                        <div className="flex items-center gap-4 mb-2" data-testid="users-page-tabs">
                            <button type="button" data-testid="users-tab-users" onClick={() => setTab("users")}
                                className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase transition-colors"
                                style={{ borderBottom: tab === "users" ? "2px solid var(--brand-accent, #C75A1B)" : "2px solid transparent", color: tab === "users" ? "#6F6A64" : "#B7AC93" }}>
                                Users
                            </button>
                            <button type="button" data-testid="users-tab-roles" onClick={() => setTab("roles")}
                                className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase transition-colors"
                                style={{ borderBottom: tab === "roles" ? "2px solid var(--brand-accent, #C75A1B)" : "2px solid transparent", color: tab === "roles" ? "#6F6A64" : "#B7AC93" }}>
                                Roles
                            </button>
                            <button type="button" data-testid="users-tab-review" onClick={() => setTab("review")}
                                className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase transition-colors"
                                style={{ borderBottom: tab === "review" ? "2px solid var(--brand-accent, #C75A1B)" : "2px solid transparent", color: tab === "review" ? "#6F6A64" : "#B7AC93" }}>
                                Report Review
                            </button>
                        </div>
                    )}
                    <SectionHeader tag="Admin · Users & Roles" title={tab === "roles" ? "Client role labels" : tab === "review" ? "Report review routing" : "Internal team and portal users"}
                                   sub={tab === "roles" ? `${roleLabels.length} client role labels` : tab === "review" ? "Who must approve each report type" : `${users.length} active users · ${Object.keys(ROLE_LABELS).length} roles`} />
                </div>
                {tab === "users" && canManage && <button data-testid="users-new-button" onClick={() => setDialog({ mode: "create", u: {} })}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}><Plus className="w-3.5 h-3.5" /> New user</button>}
            </div>
            {tab === "roles" ? (
                <RoleLabelsPanel onChange={setRoleLabels} />
            ) : tab === "review" ? (
                <ReviewRoutingPanel />
            ) : (
            <>
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-white border rounded-[8px] px-3 py-1.5 flex-1 min-w-[200px] max-w-[320px]" style={{ borderColor: "#E5E0DA" }}>
                    <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                    <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, title…" className="flex-1 bg-transparent outline-none text-[12.5px]" />
                </div>
                <select data-testid="users-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-9 px-3 rounded-lg border bg-white text-sm" style={{ borderColor: "#E5E0DA" }}>
                    <option value="All">All roles</option>

                    {Object.entries(ROLE_LABELS).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
                </select>
            </div>
            {canManage && selected.length > 0 && (
                <div data-testid="users-bulk-bar" className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border" style={{ background: "#FBEFE5", borderColor: "var(--brand-accent,#C75A1B)" }}>
                    <span className="text-[12.5px] font-semibold text-[#9F4212]">{selected.length} selected</span>
                    <div className="flex items-center gap-2">
                        <button data-testid="users-bulk-clear" onClick={() => setSelected([])} className="px-3 py-1.5 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>Clear</button>
                        <button data-testid="users-bulk-send-credentials" onClick={() => bulkSendCredentials(selected)} disabled={busyBulk}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-[8px] text-[12px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {busyBulk ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Email login details ({selected.length})
                        </button>
                    </div>
                </div>
            )}
            <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                <table className="w-full text-[12.7px]">
                    <thead>
                        <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                            {canManage && (
                                <th className="px-3 py-2.5 w-9">
                                    <input data-testid="users-select-all" type="checkbox"
                                        checked={sorted.length > 0 && sorted.every((u) => selected.includes(u.id))}
                                        onChange={(e) => setSelected(e.target.checked ? sorted.map((u) => u.id) : [])} />
                                </th>
                            )}
                            <SortableTh myKey="name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="users-th-name" className="group">Name</SortableTh>
                            <SortableTh myKey="email" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="users-th-email" className="group">Email</SortableTh>
                            <SortableTh myKey="role" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="users-th-role" className="group">Role</SortableTh>
                            <SortableTh myKey="title" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="users-th-title" className="group">Title</SortableTh>
                            <SortableTh myKey="last_login" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="users-th-last-login" className="group">Last login</SortableTh>
                            {canManage && <th className="px-3 py-2.5"></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t hover:bg-[#FCFBF9]" style={{ borderColor: "#F0EDE8" }}>
                                {canManage && (
                                    <td className="px-3 py-3">
                                        <input data-testid={`user-select-${u.id}`} type="checkbox"
                                            checked={selected.includes(u.id)} onChange={() => toggleSelect(u.id)} />
                                    </td>
                                )}
                                <td className="px-5 py-3 font-semibold text-[#1F1F1F]">
                                    <div className="flex items-center gap-2">
                                        <span className="w-7 h-7 rounded-full grid place-items-center text-[10.5px] font-bold bg-[#FBEFE5] text-[#9F4212] border border-[var(--brand-accent,_#C75A1B)]">{u.avatar_initials}</span>
                                        {u.name}
                                    </div>
                                </td>
                                <td className="px-3 py-3 text-[#2D2D2D]"><span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3 text-[#9A968F]" />{u.email}</span></td>
                                <td className="px-3 py-3">
                                    <div className="flex flex-col gap-1 items-start">
                                        <RoleChip role={u.role} />
                                        {u.role_label_id && labelById[u.role_label_id] && (
                                            <span data-testid={`user-role-label-${u.id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>
                                                {labelById[u.role_label_id]}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{u.title || <span className="italic text-[#9A968F]">—</span>}</td>
                                <td className="px-3 py-3 text-[11.5px]" style={{ color: "#6F6A64" }}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : <span className="italic">never</span>}</td>
                                {canManage && (
                                    <td className="px-3 py-3 text-right whitespace-nowrap">
                                        <button data-testid={`user-edit-${u.id}`} onClick={() => setDialog({ mode: "edit", u })} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0] inline-grid align-middle" title="Edit"><Pencil className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /></button>
                                        <button data-testid={`user-send-credentials-${u.id}`} onClick={() => sendCredentials(u)} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0] inline-grid align-middle" title="Email login details (new temp password)"><Send className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /></button>
                                        <button data-testid={`user-reset-${u.id}`} onClick={() => sendReset(u)} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0] inline-grid align-middle" title="Send reset link"><KeyRound className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /></button>
                                        {u.id !== user.id && <button data-testid={`user-delete-${u.id}`} onClick={() => deactivate(u)} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#FBE8E5] inline-grid align-middle" title="Deactivate"><Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} /></button>}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {dialog && <UserDialog mode={dialog.mode} u={dialog.u} clients={clients} roleLabels={roleLabels} onClose={() => setDialog(null)} onSaved={(res) => { setDialog(null); load(); if (res) setResult(res); }} />}
            {result && <CredentialResultDialog data={result} onClose={() => setResult(null)} />}
            {bulkResult && <BulkCredentialsDialog data={bulkResult} onClose={() => setBulkResult(null)} />}
            </>
            )}
        </div>
    );
}

function CredentialResultDialog({ data, onClose }) {
    const isCreate = data.mode === "create";
    const isReset = data.mode === "reset";
    const value = isReset ? data.reset_link : data.temp_password;
    const emailed = data.email_status === "sent";
    const title = isCreate ? "User created" : isReset ? "Reset link generated" : "Login details sent";
    const label = isReset ? "Reset link (expires in 1 hour)" : "Temporary password";
    function copy() {
        try { navigator.clipboard?.writeText(value || ""); toast.success("Copied to clipboard"); }
        catch { toast.error("Copy failed — select the text manually"); }
    }
    return (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }} onClick={onClose}>
            <div data-testid="credential-result-dialog" onClick={(e) => e.stopPropagation()} className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[480px] border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-3 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h2 className="font-display text-[17px] font-bold text-[#1F1F1F]">{title}</h2>
                    <button onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-5 space-y-3">
                    <div className="text-[12.5px]" style={{ color: "#4a4641" }}>
                        For <strong>{data.email}</strong>.{" "}
                        {emailed
                            ? "An email has been sent with these details."
                            : "Email couldn't be delivered — share the details below securely."}
                    </div>
                    <div>
                        <span className="block text-[10.5px] font-bold uppercase tracking-wide mb-1" style={{ color: "#6F6A64" }}>{label}</span>
                        <div className="flex items-center gap-2">
                            <code data-testid="credential-value" className="flex-1 px-3 py-2 rounded-[8px] text-[12px] break-all" style={{ background: "#F6F4F0", color: "#1F1F1F" }}>{value}</code>
                            <button data-testid="credential-copy" onClick={copy} className="px-3 py-2 rounded-[8px] text-[12px] font-semibold border hover:bg-[#F6F4F0] whitespace-nowrap" style={{ borderColor: "#E5E0DA" }}>Copy</button>
                        </div>
                    </div>
                    <div className="text-[11px] px-3 py-2 rounded-[8px]" style={{ background: emailed ? "#E8F1E6" : "#FCF1DC", color: emailed ? "#3F7D3A" : "#9A6B12" }}>
                        {emailed ? "Delivered via email." : `Email status: ${data.email_status}. Check the Email Outbox for details.`}
                        {!isReset && " The user will be prompted to set their own password on first login."}
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex justify-end" style={{ borderColor: "#F0EDE8" }}>
                    <button data-testid="credential-done" onClick={onClose} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>Done</button>
                </div>
            </div>
        </div>
    );
}

function BulkCredentialsDialog({ data, onClose }) {
    const rows = data.results || [];
    function copyAll() {
        const text = rows.filter((r) => r.temp_password).map((r) => `${r.email}\t${r.temp_password}`).join("\n");
        try { navigator.clipboard?.writeText(text); toast.success("All login details copied"); }
        catch { toast.error("Copy failed — select the text manually"); }
    }
    return (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }} onClick={onClose}>
            <div data-testid="bulk-credentials-dialog" onClick={(e) => e.stopPropagation()} className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[600px] border flex flex-col max-h-[85vh]" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-3 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <div>
                        <h2 className="font-display text-[17px] font-bold text-[#1F1F1F]">Login details sent</h2>
                        <p className="text-[11.5px] mt-0.5" style={{ color: "#6F6A64" }}>{data.sent} emailed · {data.queued} not delivered · {data.count} total</p>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-4 overflow-y-auto">
                    <div className="text-[11px] px-3 py-2 rounded-[8px] mb-3" style={{ background: "#FCF1DC", color: "#9A6B12" }}>
                        Each user was reset to a new temporary password and prompted to change it at next login. Copy any that weren't emailed and share them securely.
                    </div>
                    <div className="border rounded-[10px] overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                        <table className="w-full text-[12px]">
                            <thead><tr className="bg-[#F6F4F0] text-[10px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                                <th className="px-3 py-2 text-left">User</th><th className="px-3 py-2 text-left">Temp password</th><th className="px-3 py-2 text-left">Status</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} data-testid={`bulk-cred-row-${r.id}`} className="border-t" style={{ borderColor: "#F0EDE8" }}>
                                        <td className="px-3 py-2 text-[#2D2D2D]"><div className="font-semibold text-[#1F1F1F]">{r.name || "—"}</div><div className="text-[10.5px] text-[#9A968F]">{r.email}</div></td>
                                        <td className="px-3 py-2"><code className="px-2 py-1 rounded-[6px] text-[11px]" style={{ background: "#F6F4F0" }}>{r.temp_password || "—"}</code></td>
                                        <td className="px-3 py-2">{r.email_status === "sent"
                                            ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#3F7D3A" }}><CheckCircle2 className="w-3.5 h-3.5" /> Emailed</span>
                                            : <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#9A6B12" }}><AlertCircle className="w-3.5 h-3.5" /> {r.email_status}</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <button data-testid="bulk-cred-copy-all" onClick={copyAll} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>Copy all</button>
                    <button data-testid="bulk-cred-done" onClick={onClose} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>Done</button>
                </div>
            </div>
        </div>
    );
}

export function EmailOutboxPage() {
    const { user } = useAuth();
    const canManage = user && ADMIN_ROLES.has(user.role);
    const [data, setData] = useState({ items: [], counts: {}, total: 0 });
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState(null);

    const load = () => {
        setLoading(true);
        api.get(`/email-outbox`, { params: filter === "all" ? {} : { status: filter } })
            .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    async function retry(m) {
        setRetrying(m.id);
        try {
            const { data: res } = await api.post(`/email-outbox/${m.id}/retry`);
            toast[res.status === "sent" ? "success" : "message"](res.status === "sent" ? "Email re-sent" : `Retry ${res.status}`);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setRetrying(null); }
    }

    const STATUS = {
        sent: { label: "Sent", color: "#3F7D3A", bg: "#E8F1E6", Icon: CheckCircle2 },
        queued_stub: { label: "Not delivered", color: "#9A6B12", bg: "#FCF1DC", Icon: AlertCircle },
    };
    const tabs = [
        { id: "all", label: `All (${data.total || 0})` },
        { id: "sent", label: `Sent (${data.counts?.sent || 0})` },
        { id: "queued_stub", label: `Not delivered (${data.counts?.queued_stub || 0})` },
    ];

    return (
        <div data-testid="email-outbox-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <SectionHeader tag="Admin · Email" title="Email Outbox"
                    sub="Every outbound email with its Microsoft Graph delivery status" />
                <button data-testid="email-outbox-refresh" onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                {tabs.map((t) => (
                    <button key={t.id} data-testid={`email-outbox-tab-${t.id}`} onClick={() => setFilter(t.id)}
                        className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${filter === t.id ? "text-white" : "bg-white text-[#4a4641] hover:bg-[#F6F4F0]"}`}
                        style={filter === t.id ? { background: "var(--brand-accent,#C75A1B)", borderColor: "var(--brand-accent,#C75A1B)" } : { borderColor: "#E5E0DA" }}>{t.label}</button>
                ))}
            </div>
            <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                {loading ? (
                    <div className="py-16 grid place-items-center text-[#9A968F]"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : data.items.length === 0 ? (
                    <div className="py-16 grid place-items-center text-center text-[#9A968F]">
                        <Inbox className="w-8 h-8 mb-2" /><span className="text-[13px]">No emails to show</span>
                    </div>
                ) : (
                    <table className="w-full text-[12.7px]">
                        <thead>
                            <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                                <th className="px-5 py-2.5 text-left">Recipient</th>
                                <th className="px-3 py-2.5 text-left">Subject</th>
                                <th className="px-3 py-2.5 text-left">Status</th>
                                <th className="px-3 py-2.5 text-left">Sent</th>
                                {canManage && <th className="px-3 py-2.5"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((m) => {
                                const s = STATUS[m.status] || { label: m.status || "unknown", color: "#6F6A64", bg: "#F0EDE8", Icon: Clock };
                                const to = Array.isArray(m.to) ? m.to.join(", ") : m.to;
                                return (
                                    <tr key={m.id} data-testid={`email-row-${m.id}`} className="border-t hover:bg-[#FCFBF9] align-top" style={{ borderColor: "#F0EDE8" }}>
                                        <td className="px-5 py-3 text-[#2D2D2D]"><span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3 text-[#9A968F]" />{to}</span></td>
                                        <td className="px-3 py-3 text-[#1F1F1F] font-semibold max-w-[280px]">
                                            {m.subject}
                                            {m.status !== "sent" && m.error && <div className="text-[10.5px] font-normal text-[#B33A2E] mt-0.5 break-words" title={m.error}>{String(m.error).slice(0, 140)}</div>}
                                        </td>
                                        <td className="px-3 py-3"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold" style={{ background: s.bg, color: s.color }}><s.Icon className="w-3 h-3" /> {s.label}</span>{m.provider && <span className="ml-1.5 text-[10px] text-[#9A968F] uppercase">{m.provider}</span>}</td>
                                        <td className="px-3 py-3 text-[11px]" style={{ color: "#6F6A64" }}>{m.created_at ? new Date(m.created_at).toLocaleString() : "—"}</td>
                                        {canManage && (
                                            <td className="px-3 py-3 text-right whitespace-nowrap">
                                                {m.status !== "sent" && (
                                                    <button data-testid={`email-retry-${m.id}`} onClick={() => retry(m)} disabled={retrying === m.id}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[7px] text-[11.5px] font-semibold border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                                        {retrying === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Retry
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function UserDialog({ mode, u, clients, roleLabels = [], onClose, onSaved }) {
    const [form, setForm] = useState({
        name: u.name || "", email: u.email || "", role: u.role || "consultant",
        title: u.title || "", role_label_id: u.role_label_id || "", password: "", is_active: u.is_active !== false,
        can_manage_forms: u.can_manage_forms !== false,
        client_id: u.client_id || "",
        client_ids: u.client_ids && u.client_ids.length ? u.client_ids : (u.client_id ? [u.client_id] : []),
        site_ids: u.site_ids || [],
        project_ids: u.project_ids || [],
        email_opt_out_categories: u.email_opt_out_categories || [],
    });
    const [busy, setBusy] = useState(false);
    const [sites, setSites] = useState([]);
    const [projects, setProjects] = useState([]);
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const isPortal = !INTERNAL.has(form.role);
    const isAllProjects = form.role === "client_admin";

    // A single merged "Role" control shows BOTH the underlying permission roles
    // (director/administrator/consultant/client_admin/site_manager/reviewer) AND
    // every defined Client role label together, so any of them can be picked
    // directly in one place. Picking a label sets the underlying permission role
    // to match it; picking a permission role directly clears any label.
    function onRoleSelectChange(value) {
        if (value.startsWith("label:")) {
            const lbl = roleLabels.find((l) => l.id === value.slice(6));
            if (lbl) { set("role", lbl.base_role); set("role_label_id", lbl.id); }
        } else {
            set("role", value.slice(5));
            set("role_label_id", "");
        }
    }

    // Keep primary client inside the selected clients set
    const selectedClients = useMemo(() => {
        const ids = new Set(form.client_ids);
        if (form.client_id) ids.add(form.client_id);
        return Array.from(ids);
    }, [form.client_ids, form.client_id]);

    // Load sites + projects for the selected client(s) so they can be assigned
    useEffect(() => {
        if (!isPortal || selectedClients.length === 0) { setSites([]); setProjects([]); return; }
        let cancelled = false;
        Promise.all([
            Promise.all(selectedClients.map((cid) => api.get(`/sites?client_id=${cid}`).then((r) => r.data).catch(() => []))),
            Promise.all(selectedClients.map((cid) => api.get(`/projects?client_id=${cid}`).then((r) => r.data).catch(() => []))),
        ]).then(([siteArrs, projArrs]) => {
            if (cancelled) return;
            setSites(siteArrs.flat());
            setProjects(projArrs.flat());
        });
        return () => { cancelled = true; };
    }, [isPortal, JSON.stringify(selectedClients)]);

    const toggleIn = (key, id) => set(key, form[key].includes(id) ? form[key].filter((x) => x !== id) : [...form[key], id]);

    async function save() {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        if (!form.email.trim()) { toast.error("Email is required"); return; }
        if (mode === "create" && form.password && form.password.length < 8) { toast.error("Password must be 8+ chars (or leave blank to auto-generate)"); return; }
        if (isPortal && !form.client_id) { toast.error("Portal users need a primary client"); return; }
        setBusy(true);
        const clientIds = isPortal ? selectedClients : [];
        const siteIds = isPortal ? form.site_ids.filter((id) => sites.some((s) => s.id === id)) : [];
        const projectIds = (isPortal && !isAllProjects) ? form.project_ids.filter((id) => projects.some((p) => p.id === id)) : [];
        const scope = {
            client_id: isPortal ? form.client_id : null,
            client_ids: clientIds,
            site_ids: siteIds,
            project_ids: projectIds,
            can_manage_forms: form.can_manage_forms,
        };
        try {
            if (mode === "create") {
                const body = { name: form.name, email: form.email, role: form.role, title: form.title || null, role_label_id: form.role_label_id || null, ...scope };
                if (form.password) body.password = form.password;
                const { data } = await api.post("/users", body);
                toast.success("User created");
                onSaved({ mode: "create", email: data.user?.email, temp_password: data.temp_password, email_status: data.email_status });
                return;
            } else {
                await api.patch(`/users/${u.id}`, { name: form.name, email: form.email.trim().toLowerCase(), role: form.role, title: form.title || null, role_label_id: form.role_label_id || "", is_active: form.is_active, email_opt_out_categories: form.email_opt_out_categories, ...scope });
                if (form.password) { if (form.password.length < 8) { toast.error("Password must be 8+ chars"); setBusy(false); return; } await api.post(`/users/${u.id}/reset-password`, { password: form.password }); }
                toast.success("User updated");
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="user-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[560px] flex flex-col border max-h-[90vh]" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h2 className="font-display text-[18px] font-bold text-[#1F1F1F]">{mode === "create" ? "New user" : "Edit user"}</h2>
                    <button data-testid="user-dialog-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-5 space-y-3 overflow-y-auto">
                    <F label="Name"><input data-testid="user-field-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="adm-fi" /></F>
                    <F label="Email"><input data-testid="user-field-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="adm-fi" /></F>
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Role">
                            <select data-testid="user-field-role"
                                value={form.role_label_id ? `label:${form.role_label_id}` : `role:${form.role}`}
                                onChange={(e) => onRoleSelectChange(e.target.value)} className="adm-fi">
                                <optgroup label="Permission role">
                                    {Object.entries(ROLE_LABELS).map(([id, l]) => <option key={id} value={`role:${id}`}>{l}</option>)}
                                </optgroup>
                                {roleLabels.length > 0 && (
                                    <optgroup label="Client role">
                                        {roleLabels.map((l) => <option key={l.id} value={`label:${l.id}`}>{l.name}</option>)}
                                    </optgroup>
                                )}
                            </select>
                            {form.role_label_id && (
                                <p className="text-[11px] mt-1" style={{ color: "#9A968F" }}>Permissions: "{ROLE_LABELS[form.role]}".</p>
                            )}
                        </F>
                        <F label="Title">
                            <input data-testid="user-field-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="adm-fi" />
                        </F>
                    </div>
                    {isPortal && (
                        <>
                            <F label="Primary client">
                                <select data-testid="user-field-client" value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className="adm-fi">
                                    <option value="">Select client…</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </F>
                            <MultiSelect label="Additional clients (optional)" testId="user-field-clients"
                                options={clients.filter((c) => c.id !== form.client_id).map((c) => ({ id: c.id, label: c.name }))}
                                selected={form.client_ids.filter((id) => id !== form.client_id)}
                                onToggle={(id) => toggleIn("client_ids", id)} empty="No other clients" />
                            <MultiSelect label="Assigned sites (optional)" testId="user-field-sites"
                                options={sites.map((s) => ({ id: s.id, label: s.name }))}
                                selected={form.site_ids} onToggle={(id) => toggleIn("site_ids", id)}
                                empty={selectedClients.length ? "No sites for selected client(s)" : "Pick a client first"} />
                            {isAllProjects ? (
                                <div data-testid="user-field-projects-all" className="text-[11.5px] px-3 py-2 rounded-[8px]" style={{ background: "#FCFBF9", color: "#6F6A64", border: "1px solid #F0EDE8" }}>
                                    Client Admins automatically have access to <strong>all projects</strong> for their client(s).
                                </div>
                            ) : (
                                <MultiSelect label="Assigned projects" testId="user-field-projects"
                                    options={projects.map((p) => ({ id: p.id, label: `${p.reference ? p.reference + " · " : ""}${p.name}` }))}
                                    selected={form.project_ids} onToggle={(id) => toggleIn("project_ids", id)}
                                    empty={selectedClients.length ? "No projects for selected client(s)" : "Pick a client first"} />
                            )}
                            <label className="flex items-start gap-2 text-[12.5px] font-medium text-[#2D2D2D] pt-1">
                                <input data-testid="user-field-can-manage-forms" type="checkbox" className="mt-0.5"
                                    checked={form.can_manage_forms} onChange={(e) => set("can_manage_forms", e.target.checked)} />
                                <span>
                                    Manage site forms &amp; Toolbox Talks
                                    <span className="block text-[11px] font-normal text-[#9A968F]">Lets this person create/edit their own organisation's Toolbox Talks and client-completed form templates. On by default — untick to restrict them to view-only.</span>
                                </span>
                            </label>
                        </>
                    )}
                    <F label={mode === "create" ? "Temporary password (optional — auto-generated if blank)" : "Set new password (optional)"}>
                        <input data-testid="user-field-password" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={mode === "edit" ? "Leave blank to keep current" : "Leave blank to auto-generate & email"} className="adm-fi" />
                    </F>
                    {mode === "edit" && (
                        <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D] pt-1">
                            <input data-testid="user-field-active" type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Active
                        </label>
                    )}
                    {mode === "edit" && (
                        <div className="pt-2 border-t" style={{ borderColor: "#F0EDE8" }}>
                            <EmailNotificationSwitches
                                value={form.email_opt_out_categories}
                                onChange={(v) => set("email_opt_out_categories", v)} />
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t flex items-center justify-end gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid="user-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save"}</button>
                </div>
                <style>{`.adm-fi{width:100%;padding:8px 11px;background:#fff;border:1px solid #E5E0DA;border-radius:8px;font-size:12.5px;color:#1F1F1F;outline:none;}.adm-fi:focus{border-color:#C75A1B;box-shadow:0 0 0 3px #F6E4D8;}`}</style>
            </div>
        </div>
    );
}

function MultiSelect({ label, testId, options, selected, onToggle, empty }) {
    return (
        <div>
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>{label}</span>
            <div data-testid={testId} className="max-h-[120px] overflow-y-auto rounded-[8px] border divide-y" style={{ borderColor: "#E5E0DA" }}>
                {options.length === 0 ? (
                    <div className="text-[11.5px] italic text-[#9A968F] px-3 py-2">{empty}</div>
                ) : options.map((o) => (
                    <label key={o.id} data-testid={`${testId}-opt-${o.id}`} className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-[#2D2D2D] hover:bg-[#FCFBF9] cursor-pointer">
                        <input type="checkbox" checked={selected.includes(o.id)} onChange={() => onToggle(o.id)} />
                        <span className="truncate">{o.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

function F({ label, children }) {
    return <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>{label}</span>{children}</label>;
}

export function AuditLogPage() {
    const [tab, setTab] = useState("activity");
    return (
        <div data-testid={TID.auditLogPage} className="space-y-5">
            <SectionHeader tag="Admin · Audit log" title="System-wide event log"
                           sub="Every login, view, create, update and delete on sensitive records." />
            <div className="flex items-center gap-2 border-b" style={{ borderColor: "#E5E0DA" }}>
                <button data-testid="audit-tab-activity" onClick={() => setTab("activity")}
                        className="px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors"
                        style={tab === "activity" ? { color: "#22303F", borderColor: "#22303F" } : { color: "#9A968F", borderColor: "transparent" }}>
                    Site activity
                </button>
                <button data-testid="audit-tab-all" onClick={() => setTab("all")}
                        className="px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors"
                        style={tab === "all" ? { color: "#22303F", borderColor: "#22303F" } : { color: "#9A968F", borderColor: "transparent" }}>
                    All events (raw)
                </button>
                <button data-testid="audit-tab-ask" onClick={() => setTab("ask")}
                        className="px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5"
                        style={tab === "ask" ? { color: "#22303F", borderColor: "#22303F" } : { color: "#9A968F", borderColor: "transparent" }}>
                    <Sparkles className="w-3.5 h-3.5" /> Ask AI
                </button>
            </div>
            {tab === "activity" ? <SiteActivityFeed /> : tab === "ask" ? <AuditAskPanel /> : <RawAuditLog />}
        </div>
    );
}

const SITE_ACTIVITY_CATEGORIES = [
    { id: "all", label: "All", icon: Activity, colour: "#22303F" },
    { id: "qr_issued", label: "QR issued", icon: QrCode, colour: "#8B6F2E" },
    { id: "sign_in", label: "Signed in", icon: LogIn, colour: "#1F7A4D" },
    { id: "sign_out", label: "Signed out", icon: LogOut, colour: "#8A5B00" },
    { id: "induction", label: "Inductions", icon: ShieldCheck, colour: "#2059A6" },
    { id: "toolbox", label: "Toolbox talks", icon: HardHat, colour: "#B0500F" },
    { id: "incident", label: "Incidents", icon: AlertTriangle, colour: "#C23B3B" },
    { id: "rams", label: "RAMS", icon: FileText, colour: "#5C3EA6" },
    { id: "ai", label: "AI use", icon: Sparkles, colour: "#7A3EC2" },
];
const CAT_META = Object.fromEntries(SITE_ACTIVITY_CATEGORIES.map((c) => [c.id, c]));

function SiteActivityFeed() {
    const browser = useAuditBrowser("activity");
    const { events, loading, error, filters, change } = browser;
    const { category, sort_dir: sortDir } = filters;

    return (
        <div data-testid="site-activity-feed" className="space-y-4">
            <AuditLogFilters browser={browser} prefix="site-activity" placeholder="Search name, site, client…">
                <button data-testid="site-activity-sort-toggle" onClick={() => change({ sort_dir: sortDir === "desc" ? "asc" : "desc" })}
                    className="h-9 px-2.5 rounded-[8px] border text-[12px] font-semibold inline-flex items-center gap-1.5 bg-white" style={{ borderColor: "#E5E0DA", color: "#3e4b63" }}>
                    <ArrowDownUp className="w-3.5 h-3.5" /> {sortDir === "desc" ? "Newest first" : "Oldest first"}
                </button>
            </AuditLogFilters>
            <div className="flex items-center gap-2 flex-wrap">
                {SITE_ACTIVITY_CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    const active = category === c.id;
                    return (
                        <button key={c.id} data-testid={`site-activity-category-${c.id}`} onClick={() => change({ category: c.id })}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors"
                                style={active ? { background: c.colour, color: "#fff", borderColor: c.colour } : { background: "#fff", color: "#6F6A64", borderColor: "#E5E0DA" }}>
                            <Icon className="w-3.5 h-3.5" /> {c.label}
                        </button>
                    );
                })}
            </div>
            <div className="bg-white border rounded-[11px] shadow-brand-sm divide-y" style={{ borderColor: "#E5E0DA" }}>
                {loading && <div className="px-5 py-10 text-center italic text-[13px]" style={{ color: "#9A968F" }}>Loading…</div>}
                {!loading && !error && events.length === 0 && (
                    <div data-testid="site-activity-empty" className="px-5 py-10 text-center italic text-[13px]" style={{ color: "#9A968F" }}>
                        No matching activity yet.
                    </div>
                )}
                {!loading && !error && events.map((e) => {
                    const meta = CAT_META[e.category] || CAT_META.qr_issued;
                    const Icon = meta.icon;
                    return (
                        <div key={e.id} data-testid={`site-activity-row-${e.id}`} className="flex items-start gap-3 px-5 py-3.5 hover:bg-[#FCFBF9]" style={{ borderColor: "#F0EDE8" }}>
                            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${meta.colour}1A` }}>
                                <Icon className="w-4 h-4" style={{ color: meta.colour }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] text-[#2D2D2D] leading-snug">{e.summary}</div>
                                <div className="flex items-center gap-2 mt-1 text-[11px] flex-wrap" style={{ color: "#9A968F" }}>
                                    <span>{new Date(e.created_at).toLocaleString()}</span>
                                    {e.person_name && <><span>&middot;</span><span>{e.person_name}</span></>}
                                    {e.client_name && <><span>&middot;</span><span>{e.client_name}</span></>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <AuditLogPagination browser={browser} prefix="site-activity" />
        </div>
    );
}

function RawAuditLog() {
    const browser = useAuditBrowser("raw");
    const { events, filters, change, toggleSort, loading, error, options } = browser;
    const { sort_key: sortKey, sort_dir: sortDir } = filters;

    return (
        <div data-testid="audit-raw-log" className="space-y-5">
            <AuditLogFilters browser={browser} prefix="audit" placeholder="Search person, action, entity…">
                <label className="text-[11px] font-semibold text-[#6F6A64]">Action
                    <select data-testid="audit-action-filter" value={filters.action} onChange={(e) => change({ action: e.target.value })}
                        className="h-9 block mt-1 max-w-[240px] px-3 rounded-lg border bg-white text-[12.5px] font-normal" style={{ borderColor: "#E5E0DA" }}>
                        <option value="">All actions</option>
                        {options.actions.map((action) => <option key={action} value={action}>{action}</option>)}
                    </select>
                </label>
                <label className="text-[11px] font-semibold text-[#6F6A64]">Client
                    <select data-testid="audit-client-filter" value={filters.client_id} onChange={(e) => change({ client_id: e.target.value })}
                        className="h-9 block mt-1 max-w-[220px] px-3 rounded-lg border bg-white text-[12.5px] font-normal" style={{ borderColor: "#E5E0DA" }}>
                        <option value="">All clients</option>
                        {(options.clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
                <label className="text-[11px] font-semibold text-[#6F6A64]">Project
                    <select data-testid="audit-project-filter" value={filters.project_id} onChange={(e) => change({ project_id: e.target.value })}
                        className="h-9 block mt-1 max-w-[240px] px-3 rounded-lg border bg-white text-[12.5px] font-normal" style={{ borderColor: "#E5E0DA" }}>
                        <option value="">All projects</option>
                        {(options.projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </label>
            </AuditLogFilters>
            <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-x-auto" style={{ borderColor: "#E5E0DA" }}>
                <table className="w-full text-[12.7px]">
                    <thead>
                        <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                            <SortableTh myKey="when" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="audit-th-when" className="group">When</SortableTh>
                            <SortableTh myKey="actor" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="audit-th-actor" className="group">Person</SortableTh>
                            <SortableTh myKey="role" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="audit-th-role" className="group">Role</SortableTh>
                            <SortableTh myKey="action" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="audit-th-action" className="group">Action</SortableTh>
                            <SortableTh myKey="entity" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="audit-th-entity" className="group">Entity</SortableTh>
                            <SortableTh myKey="client" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="audit-th-client" className="group">Client</SortableTh>
                            <SortableTh myKey="project" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="audit-th-project" className="group">Project</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="px-5 py-10 text-center text-[#9A968F]">Loading…</td></tr>}
                        {!loading && !error && events.map((e) => (
                            <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-t hover:bg-[#FCFBF9]" style={{ borderColor: "#F0EDE8" }}>
                                <td className="px-5 py-3 text-[11.5px] font-mono" style={{ color: "#6F6A64" }}>{new Date(e.created_at).toLocaleString()}</td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{e.person_name || e.actor_email || "System"}
                                    {e.actor_email && e.actor_email !== e.person_name && <span className="block text-[11px] text-[#9A968F]">{e.actor_email}</span>}</td>
                                <td className="px-3 py-3">{e.actor_role ? <RoleChip role={e.actor_role} /> : <span className="italic text-[#9A968F] text-[11px]">—</span>}</td>
                                <td className="px-3 py-3"><Pill tone="blue">{e.action}</Pill></td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{e.entity_type}{e.entity_id ? <span className="font-mono text-[10.5px]" style={{ color: "#9A968F" }}> · {e.entity_id.slice(0, 8)}</span> : null}</td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{e.client_name || (e.client_id ? <span className="font-mono text-[10.5px]">{e.client_id.slice(0, 8)}</span> : <span className="italic text-[#9A968F]">global</span>)}</td>
                                <td className="px-3 py-3 text-[#2D2D2D]" title={e.scope_backfilled ? "Linked retrospectively from the record this event touched" : undefined}>
                                    {e.project_name || <span className="italic text-[#9A968F]">—</span>}
                                    {e.scope_backfilled && e.project_name && <span className="ml-1 text-[10px]" style={{ color: "#9A968F" }}>(backdated)</span>}
                                </td>
                            </tr>
                        ))}
                        {!loading && !error && events.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center italic" style={{ color: "#9A968F" }}>No audit events match.</td></tr>}
                    </tbody>
                </table>
            </div>
            <AuditLogPagination browser={browser} prefix="audit" />
        </div>
    );
}

// Settings > Weekly Pulse digest > per-internal-staff "receives the Pulse digest" switch
// (director/administrator/consultant — the full internal audience; consultants
// default to excluded unless explicitly opted in here, per-client Pulse only
// ever queries director/administrator so this never widens THAT audience).
function PulseStaffOptOutList() {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        api.get("/users").then((r) => {
            const list = (Array.isArray(r.data) ? r.data : (r.data.items || []))
                .filter((u) => ["director", "administrator", "consultant"].includes(u.role) && u.is_active)
                .sort((a, b) => a.name.localeCompare(b.name));
            setStaff(list);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    async function toggle(u, receiving) {
        setBusyId(u.id);
        try {
            await api.patch(`/users/${u.id}`, { pulse_digest_opt_out: receiving });
            setStaff((cur) => cur.map((x) => x.id === u.id ? { ...x, pulse_digest_opt_out: receiving } : x));
            toast.success(receiving ? `${u.name} excluded from the Pulse digest.` : `${u.name} will receive the Pulse digest again.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update this setting.");
        } finally { setBusyId(null); }
    }

    if (loading) return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    if (!staff.length) return null;

    return (
        <div className="pt-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#9A968F" }}>Internal team receiving The Pulse</p>
            <p className="text-[11px] mb-1.5" style={{ color: "#9A968F" }}>Directors/admins/consultants who receive the per-client Pulse by default — flip a switch to exclude someone. (Portfolio Pulse recipients are picked separately, below.)</p>
            <div className="space-y-1.5" data-testid="pulse-staff-opt-out-list">
                {staff.map((u) => {
                    const receiving = !u.pulse_digest_opt_out;
                    return (
                        <div key={u.id} className="flex items-center justify-between gap-2 border rounded-[7px] px-2.5 py-1.5" style={{ borderColor: "#E5E0DA" }}>
                            <div className="min-w-0">
                                <p className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{u.name} <span className="text-[10.5px] font-normal" style={{ color: "#9A968F" }}>({ROLE_LABELS[u.role] || u.role})</span></p>
                                <p className="text-[11px] truncate" style={{ color: "#6F6A64" }}>{u.email}</p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[10.5px] font-semibold" style={{ color: receiving ? "#12A150" : "#9A968F" }}>{receiving ? "Receiving" : "Excluded"}</span>
                                <Switch data-testid={`pulse-staff-toggle-${u.id}`} checked={receiving} disabled={busyId === u.id}
                                    onCheckedChange={() => toggle(u, receiving)} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Settings > Weekly Pulse digest > per-client-contact "receives the Pulse digest"
// switch — the client_admin recipients of the per-client Pulse email, across
// every client, shown here so PR can manage their opt-outs from one place too.
function ClientPulseOptOutList() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        Promise.all([api.get("/users"), api.get("/clients")]).then(([ur, cr]) => {
            const clientsList = Array.isArray(cr.data) ? cr.data : (cr.data.items || []);
            const clientName = Object.fromEntries(clientsList.map((c) => [c.id, c.name]));
            const list = (Array.isArray(ur.data) ? ur.data : (ur.data.items || []))
                .filter((u) => u.role === "client_admin" && u.is_active)
                .map((u) => ({ ...u, client_name: clientName[u.client_id] || "\u2014" }))
                .sort((a, b) => a.client_name.localeCompare(b.client_name) || a.name.localeCompare(b.name));
            setRows(list);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    async function toggle(u, receiving) {
        setBusyId(u.id);
        try {
            await api.patch(`/users/${u.id}`, { pulse_digest_opt_out: receiving });
            setRows((cur) => cur.map((x) => x.id === u.id ? { ...x, pulse_digest_opt_out: receiving } : x));
            toast.success(receiving ? `${u.name} excluded from the Pulse digest.` : `${u.name} will receive the Pulse digest again.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update this setting.");
        } finally { setBusyId(null); }
    }

    if (loading) return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    if (!rows.length) return null;

    return (
        <div className="pt-3 mt-3 border-t" style={{ borderColor: "#EFEAE2" }}>
            <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#9A968F" }}>Client contacts receiving the Pulse</p>
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="pulse-client-opt-out-list">
                {rows.map((u) => {
                    const receiving = !u.pulse_digest_opt_out;
                    return (
                        <div key={u.id} className="flex items-center justify-between gap-2 border rounded-[7px] px-2.5 py-1.5" style={{ borderColor: "#E5E0DA" }}>
                            <div className="min-w-0">
                                <p className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{u.name} <span className="text-[10.5px] font-normal" style={{ color: "#9A968F" }}>({u.client_name})</span></p>
                                <p className="text-[11px] truncate" style={{ color: "#6F6A64" }}>{u.email}</p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[10.5px] font-semibold" style={{ color: receiving ? "#12A150" : "#9A968F" }}>{receiving ? "Receiving" : "Excluded"}</span>
                                <Switch data-testid={`pulse-client-toggle-${u.id}`} checked={receiving} disabled={busyId === u.id}
                                    onCheckedChange={() => toggle(u, receiving)} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Settings > Weekly Pulse digest > client picker + live rendered preview
// (iframe hitting the render endpoint directly) + one-off test send — either
// the standard Mon-Fri business week, or a custom date range of any length.
function PulsePreviewTool() {
    const { user } = useAuth();
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [mode, setMode] = useState("week"); // "week" | "range"
    const [weekEnd, setWeekEnd] = useState("");
    const [rangeStart, setRangeStart] = useState("");
    const [rangeEnd, setRangeEnd] = useState("");
    const [sending, setSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [sendingNow, setSendingNow] = useState(false);

    useEffect(() => {
        api.get("/clients").then((r) => {
            const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
            setClients(list);
            setClientId((cur) => cur || list[0]?.id || "");
        }).catch(() => {});
    }, []);

    const qs = () => {
        const p = new URLSearchParams();
        if (mode === "week" && weekEnd) p.set("week_end", weekEnd);
        if (mode === "range" && rangeStart && rangeEnd) { p.set("week_start", rangeStart); p.set("week_end", rangeEnd); }
        return p.toString();
    };

    const previewSrc = clientId ? `${API}/pulse/render/${clientId}${qs() ? `?${qs()}` : ""}` : null;
    const clientName = clients.find((c) => c.id === clientId)?.name || "";

    async function sendTest() {
        if (!clientId) return;
        setSending(true);
        try {
            const query = qs();
            const { data } = await api.post(`/pulse/send-test/${clientId}${query ? `?${query}` : ""}`);
            toast.success(`Test Pulse sent to you (${data.recipient_count} email${data.recipient_count === 1 ? "" : "s"}) — ${data.week_label}.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send the test digest.");
        } finally { setSending(false); }
    }

    async function openSendNowConfirm() {
        if (!clientId) return;
        setLoadingRecipients(true);
        try {
            const { data } = await api.get(`/pulse/recipients/${clientId}`);
            setRecipients(data.recipients || []);
            setConfirmOpen(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load recipients.");
        } finally { setLoadingRecipients(false); }
    }

    async function confirmSendNow() {
        setSendingNow(true);
        try {
            const query = qs();
            const { data } = await api.post(`/pulse/send-now/${clientId}${query ? `?${query}` : ""}`);
            toast.success(`Pulse sent to ${clientName} (${data.recipient_count} email${data.recipient_count === 1 ? "" : "s"}) — ${data.week_label}.`);
            setConfirmOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send the digest.");
        } finally { setSendingNow(false); }
    }

    return (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: "#EFEAE2" }}>
            <h4 className="text-[12.5px] font-bold text-[#1F1F1F] mb-2 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Preview &amp; send</h4>
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <label className="text-[11px] font-semibold text-[#6F6A64]">Client
                    <select data-testid="pulse-preview-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}
                        className="mt-1 block border rounded-[7px] px-2 py-1.5 text-[12.5px]" style={{ borderColor: "#E5E0DA" }}>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
                <div className="flex items-center gap-1 text-[11.5px] font-semibold text-[#6F6A64]">
                    <button data-testid="pulse-preview-mode-week" onClick={() => setMode("week")}
                        className="px-2.5 py-1.5 rounded-[7px] border" style={{ borderColor: "#E5E0DA", background: mode === "week" ? "#F6F4F0" : "white" }}>Business week</button>
                    <button data-testid="pulse-preview-mode-range" onClick={() => setMode("range")}
                        className="px-2.5 py-1.5 rounded-[7px] border" style={{ borderColor: "#E5E0DA", background: mode === "range" ? "#F6F4F0" : "white" }}>Custom range</button>
                </div>
                {mode === "week" ? (
                    <label className="text-[11px] font-semibold text-[#6F6A64]">Week ending (Fri)
                        <input data-testid="pulse-preview-week-end" type="date" value={weekEnd} onChange={(e) => setWeekEnd(e.target.value)}
                            className="mt-1 block border rounded-[7px] px-2 py-1.5 text-[12.5px]" style={{ borderColor: "#E5E0DA" }} />
                    </label>
                ) : (
                    <>
                        <label className="text-[11px] font-semibold text-[#6F6A64]">From
                            <input data-testid="pulse-preview-range-start" type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
                                className="mt-1 block border rounded-[7px] px-2 py-1.5 text-[12.5px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                        <label className="text-[11px] font-semibold text-[#6F6A64]">To
                            <input data-testid="pulse-preview-range-end" type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
                                className="mt-1 block border rounded-[7px] px-2 py-1.5 text-[12.5px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                    </>
                )}
                <button data-testid="pulse-preview-send-test" onClick={sendTest} disabled={sending || !clientId}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "#0E1B2A" }}>
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test to me
                </button>
                <button data-testid="pulse-preview-send-now" onClick={openSendNowConfirm} disabled={loadingRecipients || !clientId}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "#C75A1B" }}>
                    {loadingRecipients ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send now to {clientName || "client"}
                </button>
            </div>
            {previewSrc ? (
                <iframe data-testid="pulse-preview-iframe" src={previewSrc} title="Pulse digest preview"
                    className="w-full rounded-[8px] border" style={{ height: "700px", borderColor: "#E5E0DA" }} />
            ) : (
                <p className="text-[11.5px] italic" style={{ color: "#9A968F" }}>Pick a client to see a live preview.</p>
            )}

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="pulse-send-now-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Send the real Pulse digest to {clientName}?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div>
                                <p className="mb-2">This sends a real, live email — not a test — to the recipient(s) below only. No other client is touched, and this counts as {clientName}'s Pulse for this business week (the Friday batch won't re-send it).</p>
                                {recipients.length ? (
                                    <ul className="text-[12.5px] text-[#3A4553] space-y-1 mb-2" data-testid="pulse-send-now-recipient-list">
                                        {recipients.map((r) => (
                                            <li key={r.id} className="flex items-center justify-between gap-2 border-b pb-1" style={{ borderColor: "#EFEAE2" }}>
                                                <span><strong>{r.name || r.email}</strong> ({ROLE_LABELS[r.role] || r.role})</span>
                                                <span className="text-[#6F6A64]">{r.email}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-[12.5px] text-[#C23B3B] mb-2">No active, opted-in recipients found for this client — sending would do nothing.</p>
                                )}
                                {user?.email && <p className="text-[11.5px] text-[#6F6A64]">You (<strong>{user.email}</strong>) will be BCC'd once on this send{recipients.some((r) => r.email?.toLowerCase() === user.email.toLowerCase()) ? " — unless you're already one of the recipients above, in which case your own copy is enough." : "."}</p>}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="pulse-send-now-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="pulse-send-now-confirm" onClick={confirmSendNow} disabled={sendingNow || !recipients.length}>
                            {sendingNow ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5 inline" /> : null} Send now
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// Settings > Portfolio Pulse > persistent recipient picker — an explicit
// "tick exactly who's on this list" whitelist (director/administrator/
// consultant only), replacing the old role+opt-out default. Used by both the
// Friday auto-batch and manual "Send now" (which also lets you tick/untick
// for one send only, without touching this saved list — see the dialog below).
function PortfolioPulseRecipientPicker() {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        api.get("/pulse/portfolio/eligible-staff").then((r) => {
            setStaff(r.data.staff || []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    async function toggle(u) {
        const nextSelected = !u.selected;
        const ids = staff.filter((s) => (s.id === u.id ? nextSelected : s.selected)).map((s) => s.id);
        setBusyId(u.id);
        try {
            const { data } = await api.patch("/pulse/portfolio/recipients", { recipient_ids: ids });
            setStaff(data.staff || []);
            toast.success(nextSelected ? `${u.name} will receive the Portfolio Pulse.` : `${u.name} removed from the Portfolio Pulse list.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update the recipient list.");
        } finally { setBusyId(null); }
    }

    if (loading) return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    if (!staff.length) return null;

    const selectedCount = staff.filter((s) => s.selected).length;

    return (
        <div className="pt-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#9A968F" }}>
                Who receives the Portfolio Pulse <span className="font-normal">({selectedCount} selected)</span>
            </p>
            <p className="text-[11px] mb-1.5" style={{ color: "#9A968F" }}>Tick exactly who's on this list (director, administrator or consultant only — never client-portal users). This is the persistent list for the Friday auto-send.</p>
            <div className="space-y-1.5" data-testid="portfolio-pulse-recipient-picker">
                {staff.map((u) => (
                    <div key={u.id} className="flex items-center justify-between gap-2 border rounded-[7px] px-2.5 py-1.5" style={{ borderColor: "#E5E0DA" }}>
                        <div className="min-w-0">
                            <p className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{u.name} <span className="text-[10.5px] font-normal" style={{ color: "#9A968F" }}>({ROLE_LABELS[u.role] || u.role})</span></p>
                            <p className="text-[11px] truncate" style={{ color: "#6F6A64" }}>{u.email}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-[10.5px] font-semibold" style={{ color: u.selected ? "#12A150" : "#9A968F" }}>{u.selected ? "Receiving" : "Excluded"}</span>
                            <Switch data-testid={`portfolio-pulse-recipient-toggle-${u.id}`} checked={u.selected} disabled={busyId === u.id}
                                onCheckedChange={() => toggle(u)} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Settings > Portfolio Pulse (internal, all clients) > live rendered preview
// + one-off test send + "send now to internal list" — no client picker since
// this edition always covers every client tenant in one email.
function PortfolioPulsePreviewTool() {
    const { user } = useAuth();
    const [mode, setMode] = useState("week"); // "week" | "range"
    const [weekEnd, setWeekEnd] = useState("");
    const [rangeStart, setRangeStart] = useState("");
    const [rangeEnd, setRangeEnd] = useState("");
    const [sending, setSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [staff, setStaff] = useState([]); // eligible staff w/ .selected — starts from the persistent list, editable for THIS send only
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [sendingNow, setSendingNow] = useState(false);
    const [saveAsDefault, setSaveAsDefault] = useState(false);

    const qs = () => {
        const p = new URLSearchParams();
        if (mode === "week" && weekEnd) p.set("week_end", weekEnd);
        if (mode === "range" && rangeStart && rangeEnd) { p.set("week_start", rangeStart); p.set("week_end", rangeEnd); }
        return p.toString();
    };

    const previewSrc = `${API}/pulse/portfolio/render${qs() ? `?${qs()}` : ""}`;

    async function sendTest() {
        setSending(true);
        try {
            const query = qs();
            const { data } = await api.post(`/pulse/portfolio/send-test${query ? `?${query}` : ""}`);
            toast.success(`Test Portfolio Pulse sent to you (${data.recipient_count} email${data.recipient_count === 1 ? "" : "s"}) — ${data.week_label}.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send the test digest.");
        } finally { setSending(false); }
    }

    async function openSendNowConfirm() {
        setLoadingRecipients(true);
        try {
            const { data } = await api.get("/pulse/portfolio/eligible-staff");
            setStaff(data.staff || []);
            setSaveAsDefault(false);
            setConfirmOpen(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load recipients.");
        } finally { setLoadingRecipients(false); }
    }

    function toggleForThisSend(id) {
        setStaff((cur) => cur.map((s) => (s.id === id ? { ...s, selected: !s.selected } : s)));
    }

    async function confirmSendNow() {
        setSendingNow(true);
        try {
            const query = qs();
            const recipient_ids = staff.filter((s) => s.selected).map((s) => s.id);
            const { data } = await api.post(`/pulse/portfolio/send-now${query ? `?${query}` : ""}`, { recipient_ids, save_as_default: saveAsDefault });
            toast.success(`Portfolio Pulse sent (${data.recipient_count} email${data.recipient_count === 1 ? "" : "s"}) — ${data.week_label}.${data.saved_as_default ? " Also saved as your new default list." : ""}`);
            setConfirmOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send the digest.");
        } finally { setSendingNow(false); }
    }

    const selectedStaff = staff.filter((s) => s.selected);

    return (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: "#EFEAE2" }}>
            <h4 className="text-[12.5px] font-bold text-[#1F1F1F] mb-2 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Preview &amp; send</h4>
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <div className="flex items-center gap-1 text-[11.5px] font-semibold text-[#6F6A64]">
                    <button data-testid="portfolio-pulse-preview-mode-week" onClick={() => setMode("week")}
                        className="px-2.5 py-1.5 rounded-[7px] border" style={{ borderColor: "#E5E0DA", background: mode === "week" ? "#F6F4F0" : "white" }}>Business week</button>
                    <button data-testid="portfolio-pulse-preview-mode-range" onClick={() => setMode("range")}
                        className="px-2.5 py-1.5 rounded-[7px] border" style={{ borderColor: "#E5E0DA", background: mode === "range" ? "#F6F4F0" : "white" }}>Custom range</button>
                </div>
                {mode === "week" ? (
                    <label className="text-[11px] font-semibold text-[#6F6A64]">Week ending (Fri)
                        <input data-testid="portfolio-pulse-preview-week-end" type="date" value={weekEnd} onChange={(e) => setWeekEnd(e.target.value)}
                            className="mt-1 block border rounded-[7px] px-2 py-1.5 text-[12.5px]" style={{ borderColor: "#E5E0DA" }} />
                    </label>
                ) : (
                    <>
                        <label className="text-[11px] font-semibold text-[#6F6A64]">From
                            <input data-testid="portfolio-pulse-preview-range-start" type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
                                className="mt-1 block border rounded-[7px] px-2 py-1.5 text-[12.5px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                        <label className="text-[11px] font-semibold text-[#6F6A64]">To
                            <input data-testid="portfolio-pulse-preview-range-end" type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
                                className="mt-1 block border rounded-[7px] px-2 py-1.5 text-[12.5px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                    </>
                )}
                <button data-testid="portfolio-pulse-send-test" onClick={sendTest} disabled={sending}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "#0E1B2A" }}>
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test to me
                </button>
                <button data-testid="portfolio-pulse-send-now" onClick={openSendNowConfirm} disabled={loadingRecipients}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "#C75A1B" }}>
                    {loadingRecipients ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send now to internal list
                </button>
            </div>
            <iframe data-testid="portfolio-pulse-preview-iframe" src={previewSrc} title="Portfolio Pulse digest preview"
                className="w-full rounded-[8px] border" style={{ height: "700px", borderColor: "#E5E0DA" }} />

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="portfolio-pulse-send-now-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Send the real Portfolio Pulse to the internal list?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div>
                                <p className="mb-2">This sends a real, live email — not a test — to everyone ticked below only, covering every client. This counts as this business week's Portfolio Pulse (the Friday batch won't re-send it). Ticking/unticking here only affects this one send — it doesn't change the persistent list in Settings above.</p>
                                {staff.length ? (
                                    <>
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <button type="button" data-testid="portfolio-pulse-send-now-select-all" className="text-[11px] font-semibold underline" style={{ color: "#0E1B2A" }} onClick={() => setStaff((cur) => cur.map((s) => ({ ...s, selected: true })))}>Select all</button>
                                            <button type="button" data-testid="portfolio-pulse-send-now-select-none" className="text-[11px] font-semibold underline" style={{ color: "#0E1B2A" }} onClick={() => setStaff((cur) => cur.map((s) => ({ ...s, selected: false })))}>Select none</button>
                                        </div>
                                        <ul className="text-[12.5px] text-[#3A4553] space-y-1 mb-2 max-h-56 overflow-y-auto pr-1" data-testid="portfolio-pulse-send-now-recipient-list">
                                            {staff.map((r) => (
                                                <li key={r.id} className="flex items-center gap-2 border-b pb-1" style={{ borderColor: "#EFEAE2" }}>
                                                    <input type="checkbox" data-testid={`portfolio-pulse-send-now-check-${r.id}`} checked={!!r.selected} onChange={() => toggleForThisSend(r.id)} className="shrink-0" />
                                                    <span className="flex-1 truncate"><strong>{r.name || r.email}</strong> ({ROLE_LABELS[r.role] || r.role})</span>
                                                    <span className="text-[#6F6A64]">{r.email}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <p className="text-[12.5px] text-[#C23B3B] mb-2">No internal staff (director/administrator/consultant) found.</p>
                                )}
                                {user?.email && <p className="text-[11.5px] mb-2" style={{ color: "#6F6A64" }}>You (<strong>{user.email}</strong>) will be BCC'd once on this send{selectedStaff.some((r) => r.email?.toLowerCase() === user.email.toLowerCase()) ? " — unless you're already ticked above, in which case your own copy is enough." : "."}</p>}
                                <label className="flex items-center gap-2 text-[11.5px] font-medium text-[#2D2D2D]">
                                    <input type="checkbox" data-testid="portfolio-pulse-send-now-save-default" checked={saveAsDefault} onChange={(e) => setSaveAsDefault(e.target.checked)} />
                                    Also save this tick-list as the new default (used for next Friday's auto-send too)
                                </label>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="portfolio-pulse-send-now-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="portfolio-pulse-send-now-confirm" onClick={confirmSendNow} disabled={sendingNow || !selectedStaff.length}>
                            {sendingNow ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5 inline" /> : null} Send now ({selectedStaff.length})
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export function SettingsPage() {
    const { user } = useAuth();
    const canEdit = user && ADMIN_ROLES.has(user.role);
    const [settings, setSettings] = useState(null);
    const [denied, setDenied] = useState(false);
    const [busy, setBusy] = useState(false);
    const [runningRem, setRunningRem] = useState(false);
    const [settingsTab, setSettingsTab] = useState("general");
    const [runningPulse, setRunningPulse] = useState(false);
    const [runningPortfolioPulse, setRunningPortfolioPulse] = useState(false);
    const [runningActionDigest, setRunningActionDigest] = useState(false);
    const [runningEod, setRunningEod] = useState(false);
    const [backfill, setBackfill] = useState(null);
    const [bfStats, setBfStats] = useState(null);
    const bfPoll = useRef(null);
    const [termsUploading, setTermsUploading] = useState(false);
    const [termsBusy, setTermsBusy] = useState(false);
    const termsFileRef = useRef(null);

    const loadBfStats = () => api.get("/backfill/pdf-derivatives/stats").then((r) => setBfStats(r.data)).catch(() => {});
    useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); else setSettings(null); }); }, []);
    useEffect(() => {
        loadBfStats();
        api.get("/backfill/pdf-derivatives").then((r) => {
            setBackfill(r.data);
            if (r.data?.status === "running") pollBackfill();
        }).catch(() => {});
        return () => { if (bfPoll.current) clearInterval(bfPoll.current); };
    }, []);
    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

    function pollBackfill() {
        if (bfPoll.current) clearInterval(bfPoll.current);
        bfPoll.current = setInterval(async () => {
            try {
                const { data } = await api.get("/backfill/pdf-derivatives");
                setBackfill(data);
                if (data.status !== "running") {
                    clearInterval(bfPoll.current); bfPoll.current = null;
                    loadBfStats();
                    if (data.status === "done") toast.success(`Backfill complete — ${data.created} photo(s) optimised.`);
                    else if (data.status === "error") toast.error("Backfill finished with errors — see status.");
                }
            } catch { /* keep polling */ }
        }, 2500);
    }

    async function runBackfill() {
        try {
            const { data } = await api.post("/backfill/pdf-derivatives");
            setBackfill(data);
            toast.message("Optimising existing photos in the background…");
            pollBackfill();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start backfill"); }
    }

    async function save() {
        setBusy(true);
        try {
            const { data } = await api.patch("/settings", {
                org_name: settings.org_name, accent_colour: settings.accent_colour, logo_initials: settings.logo_initials,
                support_email: settings.support_email || null, reminder_enabled: settings.reminder_enabled, reminder_lead_days: Number(settings.reminder_lead_days),
                action_reminder_enabled: settings.action_reminder_enabled, action_reminder_lead_days: Number(settings.action_reminder_lead_days),
                action_overdue_cadence_critical_days: Number(settings.action_overdue_cadence_critical_days) || 1,
                action_overdue_cadence_high_days: Number(settings.action_overdue_cadence_high_days) || 1,
                action_overdue_cadence_medium_days: Number(settings.action_overdue_cadence_medium_days) || 3,
                action_overdue_cadence_low_days: Number(settings.action_overdue_cadence_low_days) || 7,
                pdf_photo_quality: settings.pdf_photo_quality || "high",
                pulse_weekly_send_enabled: settings.pulse_weekly_send_enabled,
                pulse_portfolio_send_enabled: settings.pulse_portfolio_send_enabled,
                action_weekly_digest_enabled: settings.action_weekly_digest_enabled,
                attendance_eod_reminder_enabled: settings.attendance_eod_reminder_enabled,
                attendance_eod_reminder_time: settings.attendance_eod_reminder_time || "18:00",
                wsr_mgmt_summary_enabled: settings.wsr_mgmt_summary_enabled,
            });
            setSettings(data); toast.success("Settings saved");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(false); }
    }

    async function runReminders() {
        setRunningRem(true);
        try {
            const { data } = await api.post("/actions/run-reminders");
            if (!data.enabled) toast.message("Action reminders are switched off — enable and save first.");
            else toast.success(`Reminder sweep done — ${data.emails_sent} email(s) for ${data.actions_processed} action(s).`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Run failed"); }
        finally { setRunningRem(false); }
    }

    async function runPulseNow() {
        setRunningPulse(true);
        try {
            const { data } = await api.post("/pulse/run-send-now");
            if (!data.clients_processed) toast.message("No clients are due a Pulse digest right now (none enabled, or already sent this week).");
            else toast.success(`Pulse batch done — sent to ${data.clients_processed} client(s).`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Run failed"); }
        finally { setRunningPulse(false); }
    }

    async function runPortfolioPulseNow() {
        setRunningPortfolioPulse(true);
        try {
            const { data } = await api.post("/pulse/portfolio/run-send-now");
            if (data.skipped) toast.message("Portfolio Pulse already sent this business week (use Preview & send below to force a manual send).");
            else toast.success(`Portfolio Pulse batch done — sent to ${data.recipient_count} recipient(s).`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Run failed"); }
        finally { setRunningPortfolioPulse(false); }
    }

    async function runActionDigestNow(force) {
        setRunningActionDigest(true);
        try {
            const { data } = await api.post("/actions/run-weekly-digest", null, { params: force ? { force: true } : {} });
            if (data.skipped) toast.message("Already sent this business week — use Force resend to send again.");
            else toast.success(`Weekly action summary sent to ${data.recipients_sent} assignee(s).`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Run failed"); }
        finally { setRunningActionDigest(false); }
    }

    async function runEodNow() {
        setRunningEod(true);
        try {
            const { data } = await api.post("/site-forms/attendance/eod-reminder-run-now");
            toast.success(`Reminder sweep done — ${data.sent} email(s) sent to ${data.processed} still-on-site record(s).`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Run failed"); }
        finally { setRunningEod(false); }
    }

    function openTermsPicker() { termsFileRef.current?.click(); }

    async function onPickTermsFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setTermsUploading(true);
        try {
            const { data } = await api.post("/settings/terms", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setSettings(data);
            toast.success("Standard Terms & Conditions saved");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
        finally { setTermsUploading(false); }
    }

    async function viewTerms() {
        try {
            const res = await api.get("/settings/terms/file", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { toast.error("Could not open file"); }
    }

    async function removeTerms() {
        if (!window.confirm("Remove the standard Terms & Conditions? Exported quotes will no longer include them.")) return;
        setTermsBusy(true);
        try {
            const { data } = await api.delete("/settings/terms");
            setSettings(data);
            toast.success("Removed");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Remove failed"); }
        finally { setTermsBusy(false); }
    }

    return (
        <div data-testid={TID.settingsPage} className="space-y-5 pb-44">
            <div className="flex items-center gap-4 mb-1" data-testid="settings-page-tabs">
                <button type="button" data-testid="settings-tab-general" onClick={() => setSettingsTab("general")}
                    className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase transition-colors"
                    style={{ borderBottom: settingsTab === "general" ? "2px solid var(--brand-accent, #C75A1B)" : "2px solid transparent", color: settingsTab === "general" ? "#6F6A64" : "#B7AC93" }}>
                    General
                </button>
                <button type="button" data-testid="settings-tab-digests" onClick={() => setSettingsTab("digests")}
                    className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase transition-colors"
                    style={{ borderBottom: settingsTab === "digests" ? "2px solid var(--brand-accent, #C75A1B)" : "2px solid transparent", color: settingsTab === "digests" ? "#6F6A64" : "#B7AC93" }}>
                    Weekly Digests
                </button>
            </div>
            <SectionHeader tag="Admin · Settings" title={settingsTab === "digests" ? "Weekly email digests" : "Organisation & tenant configuration"}
                           sub={settingsTab === "digests" ? "Every recurring weekly summary email, in one place." : "Branding, renewal reminders and Microsoft Entra SSO."} />
            {settingsTab === "general" ? (
            <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 text-[#1F1F1F]">Profile</h3>
                    <Field label="Name" value={user?.name} />
                    <Field label="Email" value={user?.email} />
                    <Field label="Role" value={ROLE_LABELS[user?.role]} />
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 text-[#1F1F1F]">Organisation &amp; branding</h3>
                    {denied ? <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p> : settings ? (
                        <div className="space-y-3">
                            <SField label="Organisation name"><input data-testid="settings-org-name" disabled={!canEdit} value={settings.org_name} onChange={(e) => set("org_name", e.target.value)} className="set-fi" /></SField>
                            <div className="grid grid-cols-2 gap-3">
                                <SField label="Accent colour"><input data-testid="settings-accent" disabled={!canEdit} type="color" value={settings.accent_colour} onChange={(e) => set("accent_colour", e.target.value)} className="set-fi h-9 p-1" /></SField>
                                <SField label="Logo initials"><input data-testid="settings-initials" disabled={!canEdit} maxLength={3} value={settings.logo_initials} onChange={(e) => set("logo_initials", e.target.value.toUpperCase())} className="set-fi" /></SField>
                            </div>
                            <SField label="Support email"><input data-testid="settings-support-email" disabled={!canEdit} value={settings.support_email || ""} onChange={(e) => set("support_email", e.target.value)} className="set-fi" /></SField>
                        </div>
                    ) : <Loader2 className="w-4 h-4 animate-spin" />}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><CalendarClock className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Renewal reminders</h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D]">
                                <input data-testid="settings-reminder-enabled" disabled={!canEdit} type="checkbox" checked={settings.reminder_enabled} onChange={(e) => set("reminder_enabled", e.target.checked)} /> Enable renewal reminders
                            </label>
                            <SField label="Remind this many days before expiry"><input data-testid="settings-lead-days" disabled={!canEdit} type="number" min={1} max={365} value={settings.reminder_lead_days} onChange={(e) => set("reminder_lead_days", e.target.value)} className="set-fi w-28" /></SField>
                            <Pill tone="green"><Mail className="w-3 h-3 inline mr-1" />Delivered via Microsoft 365</Pill>
                        </div>
                    )}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><CheckSquare className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Action reminders</h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>Email each open assignee <strong>and the action creator</strong> when an action is due soon or overdue, with a link to open it. Overdue actions are chased on a repeating cadence (by priority) until they're completed.</p>
                            <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D]">
                                <input data-testid="settings-action-reminder-enabled" disabled={!canEdit} type="checkbox" checked={!!settings.action_reminder_enabled} onChange={(e) => set("action_reminder_enabled", e.target.checked)} /> Enable action reminders
                            </label>
                            <SField label="Remind this many days before an action is due"><input data-testid="settings-action-lead-days" disabled={!canEdit || !settings.action_reminder_enabled} type="number" min={0} max={90} value={settings.action_reminder_lead_days ?? 3} onChange={(e) => set("action_reminder_lead_days", e.target.value)} className="set-fi w-28" /></SField>
                            <div className="pt-1">
                                <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "#9A968F" }}>Overdue chase cadence (days between reminders)</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <SField label="Critical"><input data-testid="settings-cadence-critical" disabled={!canEdit || !settings.action_reminder_enabled} type="number" min={1} max={90} value={settings.action_overdue_cadence_critical_days ?? 1} onChange={(e) => set("action_overdue_cadence_critical_days", e.target.value)} className="set-fi w-full" /></SField>
                                    <SField label="High"><input data-testid="settings-cadence-high" disabled={!canEdit || !settings.action_reminder_enabled} type="number" min={1} max={90} value={settings.action_overdue_cadence_high_days ?? 1} onChange={(e) => set("action_overdue_cadence_high_days", e.target.value)} className="set-fi w-full" /></SField>
                                    <SField label="Medium"><input data-testid="settings-cadence-medium" disabled={!canEdit || !settings.action_reminder_enabled} type="number" min={1} max={90} value={settings.action_overdue_cadence_medium_days ?? 3} onChange={(e) => set("action_overdue_cadence_medium_days", e.target.value)} className="set-fi w-full" /></SField>
                                    <SField label="Low"><input data-testid="settings-cadence-low" disabled={!canEdit || !settings.action_reminder_enabled} type="number" min={1} max={90} value={settings.action_overdue_cadence_low_days ?? 7} onChange={(e) => set("action_overdue_cadence_low_days", e.target.value)} className="set-fi w-full" /></SField>
                                </div>
                            </div>
                            {canEdit && (
                                <button data-testid="settings-run-reminders" onClick={runReminders} disabled={runningRem}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                    {runningRem ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Run reminders now
                                </button>
                            )}
                            <Pill tone="neutral">Runs automatically once a day.</Pill>
                        </div>
                    )}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><Radar className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> "Still on site?" end-of-day reminder</h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>Anyone still shown as signed in at the configured time gets an email with a one-tap sign-out link (they can set the time they actually left). Only sent to attendance records with an email on file.</p>
                            <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D]">
                                <input data-testid="settings-eod-reminder-enabled" disabled={!canEdit} type="checkbox" checked={!!settings.attendance_eod_reminder_enabled} onChange={(e) => set("attendance_eod_reminder_enabled", e.target.checked)} /> Enable the end-of-day reminder
                            </label>
                            <SField label="Reminder time (24h, UK time)"><input data-testid="settings-eod-reminder-time" disabled={!canEdit || !settings.attendance_eod_reminder_enabled} type="time" value={settings.attendance_eod_reminder_time || "18:00"} onChange={(e) => set("attendance_eod_reminder_time", e.target.value)} className="set-fi w-32" /></SField>
                            {canEdit && (
                                <button data-testid="settings-run-eod-now" onClick={runEodNow} disabled={runningEod}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                    {runningEod ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Run reminder sweep now
                                </button>
                            )}
                            <Pill tone="neutral">Checks every 15 minutes; each open sign-in only gets one nudge per day.</Pill>
                        </div>
                    )}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><FileText className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Report PDFs</h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>Photos are automatically compressed when generating inspection &amp; incident report PDFs, so reports stay fast even with many images. Choose the print quality.</p>
                            <SField label="Photo quality in PDFs">
                                <select data-testid="settings-pdf-photo-quality" disabled={!canEdit} value={settings.pdf_photo_quality || "high"} onChange={(e) => set("pdf_photo_quality", e.target.value)} className="set-fi w-full">
                                    <option value="standard">Standard — smallest files, fastest</option>
                                    <option value="high">High — recommended balance</option>
                                    <option value="maximum">Maximum — sharpest, larger files</option>
                                </select>
                            </SField>
                            {canEdit && (
                                <div className="pt-2 border-t border-[#F0EDE8]">
                                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "#9A968F" }}>Optimise existing photos</p>
                                        {bfStats && bfStats.total > 0 && (
                                            <span data-testid="settings-backfill-badge"
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold"
                                                style={{ background: bfStats.remaining > 0 ? "#FBF0E6" : "#EAF6EE", color: bfStats.remaining > 0 ? "#9A5B2A" : "#2E7D46" }}>
                                                {bfStats.remaining > 0
                                                    ? `${bfStats.optimised} optimised · ${bfStats.remaining} remaining`
                                                    : `All ${bfStats.optimised} photos optimised`}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[11.5px] mb-2" style={{ color: "#6F6A64" }}>Runs automatically after each deploy — this button re-runs it on demand. Creates fast, compressed copies for photos uploaded before this feature, so older reports generate quickly too.</p>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <button data-testid="settings-backfill-photos" onClick={runBackfill} disabled={backfill?.status === "running"}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0] disabled:opacity-60 disabled:cursor-wait" style={{ borderColor: "#E5E0DA" }}>
                                            {backfill?.status === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                            {backfill?.status === "running" ? "Optimising…" : "Optimise existing photos"}
                                        </button>
                                        {backfill && backfill.status !== "idle" && (
                                            <span data-testid="settings-backfill-status" className="text-[11.5px]" style={{ color: "#6F6A64" }}>
                                                {backfill.status === "running" && `${backfill.processed}/${backfill.total} processed…`}
                                                {backfill.status === "done" && `Done — ${backfill.created} optimised${backfill.errors ? `, ${backfill.errors} failed` : ""}.`}
                                                {backfill.status === "error" && `Finished with errors (${backfill.created} done, ${backfill.errors} failed).`}
                                            </span>
                                        )}
                                        {backfill?.status === "running" && (
                                            <button data-testid="settings-backfill-restart" onClick={runBackfill} type="button"
                                                className="text-[11px] underline text-[#9A968F] hover:text-[#6F6A64]">
                                                Stuck? Restart
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><Tags className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Enquiries &amp; Quotes</h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#9A968F" }}>Standard Terms &amp; Conditions</p>
                                <p className="text-[11.5px] mb-2" style={{ color: "#6F6A64" }}>One org-wide PDF, appended automatically to exported fee proposals when "Include standard T&amp;Cs" is checked on a quote.</p>
                                {settings.standard_terms_filename ? (
                                    <div className="flex items-center gap-2 flex-wrap mb-2">
                                        <Pill tone="green"><FileText className="w-3 h-3 inline mr-1" />{settings.standard_terms_filename}</Pill>
                                        <button data-testid="settings-terms-view" onClick={viewTerms} className="text-[11px] font-semibold underline" style={{ color: "#2A5DB0" }}>View</button>
                                        {canEdit && <button data-testid="settings-terms-remove" onClick={removeTerms} disabled={termsBusy} className="text-[11px] font-semibold underline text-[#B33A2E] disabled:opacity-60">Remove</button>}
                                    </div>
                                ) : (
                                    <p className="text-[11.5px] italic mb-2" style={{ color: "#9A968F" }}>No T&amp;Cs on file yet.</p>
                                )}
                                {canEdit && (
                                    <>
                                        <input ref={termsFileRef} type="file" accept="application/pdf" hidden onChange={onPickTermsFile} data-testid="settings-terms-file-input" />
                                        <button data-testid="settings-terms-upload" onClick={openTermsPicker} disabled={termsUploading}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11.5px] font-semibold border bg-white hover:bg-[#F6F4F0] disabled:opacity-60" style={{ borderColor: "#E5E0DA" }}>
                                            {termsUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {settings.standard_terms_filename ? "Replace PDF" : "Upload PDF"}
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="pt-2 border-t flex items-center gap-3 flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                                <Link to="/app/service-types" data-testid="settings-link-service-types" className="text-[11.5px] font-semibold underline" style={{ color: "#2A5DB0" }}>Manage Service Types</Link>
                                <Link to="/app/fee-proposal-templates" data-testid="settings-link-fee-templates" className="text-[11.5px] font-semibold underline" style={{ color: "#2A5DB0" }}>Manage Fee Proposal Templates</Link>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><ShieldCheck className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Microsoft Entra SSO</h3>
                    <p className="text-[12.5px]" style={{ color: "#6F6A64" }}>Staff can sign in with Microsoft on the login screen. SSO only signs in existing users (matched by email) — it never creates accounts. Outbound email is sent via Microsoft Graph.</p>
                    <Pill tone="green" className="mt-3"><ShieldCheck className="w-3 h-3 inline mr-1" />Connected to your Microsoft 365 tenant</Pill>
                </div>
            </div>
            ) : (
            <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><CheckSquare className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Weekly action summary</h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>
                                Every Friday, email each person a personal list of every action still open in their name — overdue ones flagged
                                first — with a link straight into each one so they can mark it complete, change the due date or add a comment.
                            </p>
                            <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D]">
                                <input data-testid="settings-action-digest-enabled" disabled={!canEdit} type="checkbox" checked={!!settings.action_weekly_digest_enabled} onChange={(e) => set("action_weekly_digest_enabled", e.target.checked)} /> Enable the Friday action summary
                            </label>
                            {canEdit && (
                                <div className="flex items-center gap-2">
                                    <button data-testid="settings-run-action-digest-now" onClick={() => runActionDigestNow(false)} disabled={runningActionDigest}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                        {runningActionDigest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send now
                                    </button>
                                    <button data-testid="settings-run-action-digest-force" onClick={() => runActionDigestNow(true)} disabled={runningActionDigest}
                                        className="text-[11.5px] font-semibold underline" style={{ color: "#6F6A64" }}>
                                        Force resend
                                    </button>
                                </div>
                            )}
                            <Pill tone="neutral">One email per business week per assignee, sent Fridays.</Pill>
                        </div>
                    )}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><Activity className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Weekly Pulse digest</h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>
                                Every Friday morning, email a branded weekly read-out to PR directors/admins and each
                                client's own admins — for every client with "Weekly Pulse email digest" turned on in
                                their client settings. Recipients can unsubscribe individually from the email footer.
                            </p>
                            <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D]">
                                <input data-testid="settings-pulse-weekly-enabled" disabled={!canEdit} type="checkbox" checked={!!settings.pulse_weekly_send_enabled} onChange={(e) => set("pulse_weekly_send_enabled", e.target.checked)} /> Enable the Friday Pulse send
                            </label>
                            {canEdit && (
                                <button data-testid="settings-run-pulse-now" onClick={runPulseNow} disabled={runningPulse}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                    {runningPulse ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Pulse batch now
                                </button>
                            )}
                            <Pill tone="neutral">Checks a few times daily on Fridays; each client only ever gets one per week.</Pill>
                            {canEdit && <PulseStaffOptOutList />}
                            {canEdit && <ClientPulseOptOutList />}
                            {canEdit && <PulsePreviewTool />}
                        </div>
                    )}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><Activity className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Portfolio Pulse <span className="text-[10.5px] font-normal px-1.5 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>internal, all clients</span></h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>
                                Every Friday morning, email a single portfolio roll-up — every client's activity, a
                                client leaderboard, and the portfolio's overdue actions — to whoever's ticked below
                                (never to client-portal users). Recipients can also unsubscribe from the email footer.
                            </p>
                            {canEdit && <PortfolioPulseRecipientPicker />}
                            <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D]">
                                <input data-testid="settings-pulse-portfolio-enabled" disabled={!canEdit} type="checkbox" checked={!!settings.pulse_portfolio_send_enabled} onChange={(e) => set("pulse_portfolio_send_enabled", e.target.checked)} /> Enable the Friday Portfolio Pulse send
                            </label>
                            {canEdit && (
                                <button data-testid="settings-run-portfolio-pulse-now" onClick={runPortfolioPulseNow} disabled={runningPortfolioPulse}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                    {runningPortfolioPulse ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Portfolio Pulse batch now
                                </button>
                            )}
                            <Pill tone="neutral">Checks a few times daily on Fridays; sends once per business week regardless of clients enabled.</Pill>
                            {canEdit && <PortfolioPulsePreviewTool />}
                        </div>
                    )}
                </div>

                <div className="bg-white border rounded-[11px] p-5 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2 text-[#1F1F1F]"><FileText className="w-4 h-4 text-[var(--brand-accent,_#C75A1B)]" /> Weekly Management Summary</h3>
                    {denied && <p className="text-[12.5px] italic" style={{ color: "#9A968F" }}>Admin-only section.</p>}
                    {settings && (
                        <div className="space-y-3">
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>
                                From Monday 08:00, releases + emails each enabled client's Management Summaries for the
                                week that just closed — one email per client per week, to that client's admins only.
                                Never backfills older weeks.
                            </p>
                            <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D]">
                                <input data-testid="settings-ms-release-enabled" disabled={!canEdit} type="checkbox" checked={!!settings.wsr_mgmt_summary_enabled} onChange={(e) => set("wsr_mgmt_summary_enabled", e.target.checked)} /> Enable the Monday Management Summary release
                            </label>
                            <Pill tone="neutral">Checked every 15 minutes from Monday 08:00; at most one email per client per week.</Pill>
                        </div>
                    )}
                </div>
            </div>
            )}
            {canEdit && settings && (
                <div className="flex justify-end">
                    <button data-testid="settings-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save settings"}</button>
                </div>
            )}
            <style>{`.set-fi{width:100%;padding:8px 11px;background:#fff;border:1px solid #E5E0DA;border-radius:8px;font-size:12.5px;color:#1F1F1F;outline:none;}.set-fi:focus{border-color:#C75A1B;box-shadow:0 0 0 3px #F6E4D8;}.set-fi:disabled{background:#FAFAF8;color:#6F6A64;}`}</style>
        </div>
    );
}

function SField({ label, children }) {
    return <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>{label}</span>{children}</label>;
}

function Field({ label, value }) {
    return (
        <div className="flex items-baseline justify-between py-2 border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
            <span className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#9A968F" }}>{label}</span>
            <span className="text-[12.5px] font-medium text-[#1F1F1F]">{value || "—"}</span>
        </div>
    );
}

function SectionHeader({ tag, title, sub }) {
    return (
        <div>
            <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>{tag}</span>
            <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">{title}</h2>
            {sub && <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>{sub}</p>}
        </div>
    );
}
